import React, { FC, useReducer, useState } from 'react';
import { useLocation } from 'react-router';
import { Form as FormikForm, Formik } from 'formik';
import { cloneDeep, compact } from 'lodash';
import { useHistory } from 'react-router';
import { Grid } from '@material-ui/core';

import { ContentInput, ManualPostMutation, Network, PostType, useManualPostMutation } from 'api';
import { UserGroup } from 'types/app';
import { useClient, useNotify } from 'hooks';
import { ContentProvider } from 'providers';
import { ShareDialog } from 'features/sharing';
import * as paths from 'paths';

import { formatBody, formatDisclosures } from '../helpers';
import { PostUIContext, initialPostUIState, postContextReducer } from '../PostContext';

import Post from './Post';
import { Sharing } from '../components/Widgets';

interface ILocationState {
  group: UserGroup;
}

const PostCreate: FC = () => {
  const notify = useNotify();
  const history = useHistory();
  const location = useLocation<ILocationState>();
  const { public_post_page } = useClient();

  const [content, setContent] = useState({});
  const [open, setOpen] = useState(false);
  const [state, dispatch] = useReducer(postContextReducer, initialPostUIState);
  const [processContent] = useManualPostMutation({
    onError: () => notify.mutationError(),
    onCompleted: handleCompleted,
  });

  const groupIds = getGroupIds();

  function getGroupIds() {
    if (location?.state?.group) {
      const { group } = location.state;
      return [group.id];
    }

    return [];
  }

  function getValues(): ContentInput {
    const searchParams = new URLSearchParams(location.search);
    const shareUrl = searchParams.get('share-url');

    return {
      group_ids: groupIds,
      content_source_id: 'manual',
      is_engageable: false,
      is_important: false,
      is_shareable: public_post_page,
      is_pinned: false,
      is_branded: false,
      pinned_end_at: null,
      lock_commentary: false,
      is_important_message: '',
      url_param_values: [],
      pinned_to_groups: [],
      ms_teams_channels: [],
      commentary: [
        { network: Network.Twitter, commentary: '' },
        { network: Network.Linkedin, commentary: '' },
        { network: Network.Facebook, commentary: '' },
      ],
      disclosures: [
        { network: Network.Twitter, disclosure_id: '' },
        { network: Network.Linkedin, disclosure_id: '' },
        { network: Network.Facebook, disclosure_id: '' },
      ],
      schedule: {
        publish: null,
        expire: null,
      },
      content_object: {
        post_type: PostType.Manual,
        body: '',
        url: shareUrl || '',
        title: '',
        photos: null,
        media: null,
        url_metadata_override: {
          title: '',
          description: '',
        },
      },
    };
  }

  function handleCompleted(mutationResult: ManualPostMutation) {
    if (!state.share) {
      notify.info({ i18n: { i18nKey: 'posting:created' } });
      location.key ? history.goBack() : history.push(paths.index);
      return;
    }

    setContent(mutationResult.processContent);
    setOpen(true);
  }

  function onSubmit(contentInput: ContentInput) {
    const contentToSubmit: ContentInput = cloneDeep(contentInput);

    contentToSubmit.group_ids = compact(contentToSubmit.group_ids);
    contentToSubmit.content_object.body = formatBody(contentToSubmit);
    contentToSubmit.commentary = contentToSubmit.commentary.filter(c => !!c.commentary);
    contentToSubmit.disclosures = formatDisclosures(contentToSubmit);

    delete contentToSubmit.content_object.url_metadata;

    const options = {
      variables: { content: contentToSubmit },
    };

    return processContent(options);
  }

  return (
    <PostUIContext.Provider value={{ state, dispatch }}>
      <Formik validateOnChange={false} onSubmit={onSubmit} initialValues={getValues()}>
        <FormikForm>
          <Post>
            <Grid item container>
              <Sharing />
            </Grid>
          </Post>
          {content && (
            <ContentProvider value={{ content }}>
              <ShareDialog onClose={() => setOpen(false)} open={open} />
            </ContentProvider>
          )}
        </FormikForm>
      </Formik>
    </PostUIContext.Provider>
  );
};

export default PostCreate;
